import { useMutation } from '@apollo/client';

import {
  AlertDreamJobDocument,
  type AlertDreamJobMutation,
  type AlertDreamJobMutationVariables,
  DeleteAlertDreamJobDocument,
  type DeleteAlertDreamJobMutation,
  type DeleteAlertDreamJobMutationVariables,
} from '@xing-com/crate-dream-machine-graphql';
import { getAlertDataForCard } from '@xing-com/crate-dream-machine-hooks';

import { useJobCardTracking } from '../../cards/job-card/use-job-card-tracking';
import { useSearchAndShuffleContext } from '../../context';

import { useErrorHandler } from './use-error-handler';

type UseSearchAlertActionsReturn = {
  createAlert: (deckIndex: number) => Promise<string | undefined>;
  deleteAlert: (alertId: string, itemUrn: string) => Promise<boolean>;
};

export const useSearchAlertActions = (): UseSearchAlertActionsReturn => {
  const { trackDeleteSearchAlert } = useJobCardTracking();
  const { decks, setStatusBanner } = useSearchAndShuffleContext();
  const { triggerError } = useErrorHandler();

  const [createAlertMutation] = useMutation<
    AlertDreamJobMutation,
    AlertDreamJobMutationVariables
  >(AlertDreamJobDocument);

  const [deleteAlertMutation] = useMutation<
    DeleteAlertDreamJobMutation,
    DeleteAlertDreamJobMutationVariables
  >(DeleteAlertDreamJobDocument);

  const createAlert = async (
    deckIndex: number
  ): Promise<string | undefined> => {
    if (
      decks[deckIndex].cards[0].type !== 'TakenJob' &&
      decks[deckIndex].cards[0].type !== 'AvailableJob'
    ) {
      return undefined;
    }

    const alert = getAlertDataForCard(decks[deckIndex].cards[0].data);
    if (!(alert?.keywords && alert?.location)) {
      return undefined;
    }

    const result = await createAlertMutation({
      variables: { keywords: alert.keywords, location: alert.location },
    });

    if (
      result.data?.createSearchAlertByQueryNew?.__typename !==
      'SearchAlertErrorResponse'
    ) {
      setStatusBanner({
        show: true,
        key: 'SEARCH_AND_SHUFFLE_STATUS_BANNER_REMINDER_ENABLED',
        type: 'success',
      });

      return result.data?.createSearchAlertByQueryNew?.__typename ===
        'SearchAlertCreationSuccess'
        ? result.data.createSearchAlertByQueryNew.alert.id
        : undefined;
    }

    // const alreadyExists =
    //   result.data?.createSearchAlertByQueryNew?.code === 'ALERT_EXISTS_ALREADY';

    // tracking https://new-work.atlassian.net/browse/UIC-9225 - item 7
    triggerError({
      element: 'search_alert',
      context: `search_alert_activation_error_${result.data?.createSearchAlertByQueryNew?.code || 'unknown'}`,
      position: deckIndex,
      errorStatusBannerKey: 'DREAM_MACHINE_ERROR_GENERIC_STATUS_BANNER',
    });

    return undefined;
  };

  const deleteAlert = async (
    alertId: string,
    itemUrn: string
  ): Promise<boolean> => {
    if (!alertId) {
      return false;
    }

    const response = await deleteAlertMutation({
      variables: { searchAlertId: alertId },
    });

    trackDeleteSearchAlert({ itemUrn });

    if (
      response.data?.deleteSearchAlert?.__typename !==
      'SearchAlertErrorResponse'
    ) {
      setStatusBanner({
        show: true,
        key: 'SEARCH_AND_SHUFFLE_STATUS_BANNER_REMINDER_DISABLED',
        type: 'success',
      });
      return true;
    }

    return false;
  };

  return {
    createAlert,
    deleteAlert,
  };
};
